/**
 * FlowRunViewer
 * ─────────────────────────────────────────────────────────────────────────────
 * Read-only React Flow canvas that shows live per-node execution status while
 * a production flow run is in progress.
 *
 * Props
 *   flowSnapshot   - { nodes, edges } from Content.flow_snapshot / structure API
 *   executionId    - current run's UUID (from /tenant-portal/runs endpoint)
 *   isExecuting    - true while the run hasn't reached a terminal state
 *   stepStatus     - { [nodeId]: { status, rows_affected, duration, error_message } }
 *                    provided by TenantAppRunnerPage's polling loop (already exists)
 *   onClose        - callback to hide the canvas and return to the console view
 */

import React, { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    ReactFlowProvider,
    type Node,
    type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ── Reuse ALL existing node renderers from FlowCanvas ────────────────────────
// We import the private nodeTypes map indirectly via a thin re-export wrapper.
// Since FlowCanvas doesn't export nodeTypes, we duplicate only the registry
// (no node component code is duplicated — they're all imported from their own files).
import SmartTransformNode from './nodes/SmartTransformNode';
import { DataProfileNode } from './nodes/DataProfileNode';
import SourceNode from './nodes/SourceNode';
import FilterNode from './nodes/FilterNode';
import DestinationNode from './nodes/DestinationNode';
import { BaseNodeUI } from './nodes/BaseNodeUI';
import JoinNode from './nodes/JoinNode';
import ConditionalSplitNode from './nodes/ConditionalSplitNode';
import AnnotationNode from './nodes/AnnotationNode';
import ContainerNode from './nodes/ContainerNode';
import VisualNode from './nodes/VisualNode';
import { DateNode } from './nodes/DateNode';
import GeoNode from './nodes/GeoNode';
import CurrencyNode from './nodes/CurrencyNode';
import EdiSourceNode from './nodes/EdiSourceNode';
import RemoteFileNode from './nodes/RemoteFileNode';
import { ApprovalNode } from './nodes/ApprovalNode';
import { FetchDocumentNode } from './nodes/FetchDocumentNode';
import SignalStartNode from './nodes/SignalStartNode';
import { UniversalApiNode } from './nodes/UniversalApiNode';
import { AIAgentNode } from './nodes/AIAgentNode';
import FlowRunnerNode from './nodes/FlowRunnerNode';
import EmailListenerTriggerNode from './nodes/EmailListenerTriggerNode';
import ForEachNode from './nodes/ForEachNode';
import BarrierNode from './nodes/BarrierNode';
import MultiJoinNode from './nodes/MultiJoinNode';
import TeamsNode from './nodes/TeamsNode';
import { UserActionNode } from './nodes/UserActionNode';
import WebhookNode from './nodes/WebhookNode';
import GenerateDocumentNode from './nodes/GenerateDocumentNode';
import FileListenerNode from './nodes/FileListenerNode';
import DocumentParserNode from './nodes/DocumentParserNode';
import ConditionalRouterNode from './nodes/ConditionalRouterNode';
import DataMapperNode from './nodes/DataMapperNode';
import DocumentGeneratorNode from './nodes/DocumentGeneratorNode';
import DuplicateDetectorNode from './nodes/DuplicateDetectorNode';
import PythonScriptNode from './nodes/PythonScriptNode';
import LoopNode from './nodes/LoopNode';
import ContentMergerNode from './nodes/ContentMergerNode';
import AISummarizerNode from './nodes/AISummarizerNode';
import DatabaseLookupNode from './nodes/DatabaseLookupNode';
import TemplateRendererNode from './nodes/TemplateRendererNode';
import FileOutputNode from './nodes/FileOutputNode';
import LLMPromptNode from './nodes/LLMPromptNode';
import RelaySourceNode from './nodes/RelaySourceNode';
import RelaySinkNode from './nodes/RelaySinkNode';
import EmailAttributeFilterNode from './nodes/EmailAttributeFilterNode';
import EmailPatternFilterNode from './nodes/EmailPatternFilterNode';
import AttachmentFilterNode from './nodes/AttachmentFilterNode';
import AttachmentProcessorNode from './nodes/AttachmentProcessorNode';
import SalesforceNode from './nodes/SalesforceNode';
import HubSpotNode from './nodes/HubSpotNode';
import FileSourceNode from './nodes/FileSourceNode';
import FileObjectNode from './nodes/FileObjectNode';

import { memo } from 'react';
import {
    FileOutput, Sigma, Layers, TableProperties, FunctionSquare, LayoutGrid, LayoutList,
    ScanSearch, Database, BrainCircuit, Zap, BarChart, Cpu, ArrowDownWideNarrow, CopyMinus,
    Trophy, History, Wand2, Mail, Send, FileSearch, UserCheck, Calendar, FilePen, Package,
    X, Maximize2, Minimize2, Play, CheckCircle2, XCircle, Loader2, Clock, SkipForward,
    GitBranch, Eye,
} from 'lucide-react';

// ── Inline mini-node types (same as FlowCanvas) ──────────────────────────────
const TrainModelNode      = memo(({ data, selected }: any) => <BaseNodeUI title="Train Model"      icon={BrainCircuit}       colorClass="" bgClass="bg-gradient-to-br from-violet-500 to-violet-700"  selected={selected} executionStatus={data?.executionStatus} />);
const TrainedModelNode    = memo(({ data, selected }: any) => <BaseNodeUI title="Trained Model"    icon={Package}            colorClass="" bgClass="bg-gradient-to-br from-emerald-500 to-emerald-700" selected={selected} executionStatus={data?.executionStatus} />);
const EmailTriggerNode    = memo(({ data, selected }: any) => <BaseNodeUI title="Email Source"     icon={Mail}               colorClass="" bgClass="bg-gradient-to-br from-blue-400 to-blue-600"       selected={selected} executionStatus={data?.executionStatus} isSource />);
const EmailActionNode     = memo(({ data, selected }: any) => <BaseNodeUI title="Send Email"       icon={Send}               colorClass="" bgClass="bg-gradient-to-br from-green-500 to-green-700"     selected={selected} executionStatus={data?.executionStatus} />);
const AggregateNode       = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Aggregate"} icon={Sigma}    colorClass="" bgClass="bg-gradient-to-br from-purple-500 to-purple-700"   selected={selected} executionStatus={data?.executionStatus} />);
const FormulaNode         = memo(({ data, selected }: any) => <BaseNodeUI title="Formula"          icon={FunctionSquare}     colorClass="" bgClass="bg-gradient-to-br from-blue-500 to-blue-700"       selected={selected} executionStatus={data?.executionStatus} />);
const OutputNode          = memo(({ data, selected }: any) => <BaseNodeUI title="Output Data"      icon={FileOutput}         colorClass="" bgClass="bg-gradient-to-br from-teal-500 to-teal-700"       selected={selected} executionStatus={data?.executionStatus} isSource={false} />);
const CleanNode           = memo(({ data, selected }: any) => <BaseNodeUI title="Cleanse"          icon={Layers}             colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700"   selected={selected} executionStatus={data?.executionStatus} />);
const ColumnNode          = memo(({ data, selected }: any) => <BaseNodeUI title="Columns"          icon={TableProperties}    colorClass="" bgClass="bg-gradient-to-br from-yellow-500 to-yellow-700"   selected={selected} executionStatus={data?.executionStatus} />);
const PivotNode           = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Pivot"} icon={LayoutGrid}    colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700"   selected={selected} executionStatus={data?.executionStatus} />);
const UnpivotNode         = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Unpivot"} icon={LayoutList}  colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700"   selected={selected} executionStatus={data?.executionStatus} />);
const DeduplicateNode     = memo(({ data, selected }: any) => <BaseNodeUI title="Deduplicate"      icon={CopyMinus}          colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700"   selected={selected} executionStatus={data?.executionStatus} />);
const SortNode            = memo(({ data, selected }: any) => <BaseNodeUI title="Sort"             icon={ArrowDownWideNarrow} colorClass="" bgClass="bg-gradient-to-br from-teal-500 to-teal-700"     selected={selected} executionStatus={data?.executionStatus} />);
const AutoTypeNode        = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Auto Type"} icon={ScanSearch} colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700"  selected={selected} executionStatus={data?.executionStatus} />);
const UnionNode           = memo(({ data, selected }: any) => <BaseNodeUI title="Union"            icon={Layers}             colorClass="" bgClass="bg-gradient-to-br from-purple-500 to-purple-700"   selected={selected} executionStatus={data?.executionStatus} />);
const ExecuteSqlNode      = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Execute SQL"} icon={Database} colorClass="" bgClass="bg-gradient-to-br from-slate-600 to-slate-800"  selected={selected} executionStatus={data?.executionStatus} />);
const SqlDmlNode          = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "SQL DML"}    icon={FilePen}  colorClass="" bgClass="bg-gradient-to-br from-slate-600 to-slate-800"    selected={selected} executionStatus={data?.executionStatus} isSource={false} />);
const ScoreModelNode      = memo(({ data, selected }: any) => <BaseNodeUI title="Score Model"      icon={Zap}                colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700"     selected={selected} executionStatus={data?.executionStatus} />);
const PredictModelNode    = memo(({ data, selected }: any) => <BaseNodeUI title="Predict"          icon={Zap}                colorClass="" bgClass="bg-gradient-to-br from-amber-500 to-amber-700"     selected={selected} executionStatus={data?.executionStatus} />);
const ParetoNode          = memo(({ data, selected }: any) => <BaseNodeUI title="Pareto"           icon={Trophy}             colorClass="" bgClass="bg-gradient-to-br from-orange-500 to-orange-700"   selected={selected} executionStatus={data?.executionStatus} />);
const WindowNode          = memo(({ data, selected }: any) => <BaseNodeUI title="Window"           icon={History}            colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700"   selected={selected} executionStatus={data?.executionStatus} />);
const EvaluateModelNode   = memo(({ data, selected }: any) => <BaseNodeUI title="Evaluate"         icon={BarChart}           colorClass="" bgClass="bg-gradient-to-br from-cyan-500 to-cyan-700"       selected={selected} executionStatus={data?.executionStatus} />);
const AiInsightNode       = memo(({ data, selected }: any) => <BaseNodeUI title="AI Insight"       icon={Cpu}                colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-indigo-700"   selected={selected} executionStatus={data?.executionStatus} />);
const MdmIngestNode       = memo(({ data, selected }: any) => <BaseNodeUI title="MDM Ingest"       icon={Database}           colorClass="" bgClass="bg-gradient-to-br from-indigo-600 to-indigo-800"   selected={selected} executionStatus={data?.executionStatus} isSource={false} />);
const SmartImputeNode     = memo(({ data, selected }: any) => <BaseNodeUI title="Smart Impute"     icon={Wand2}              colorClass="" bgClass="bg-gradient-to-br from-indigo-500 to-purple-600"   selected={selected} executionStatus={data?.executionStatus} />);
const DataModelNode       = memo(({ data, selected }: any) => <BaseNodeUI title={data.label || "Data Model"} icon={Database} colorClass="" bgClass="bg-gradient-to-br from-blue-600 to-indigo-700"    selected={selected} executionStatus={data?.executionStatus} />);
const ScheduleNode        = memo(({ data, selected }: any) => <BaseNodeUI title="Schedule"         icon={Calendar}           colorClass="" bgClass="bg-gradient-to-br from-slate-500 to-slate-700"     selected={selected} executionStatus={data?.executionStatus} isSource />);
const SharePointAccessNode = memo(({ data, selected }: any) => <BaseNodeUI title="SharePoint Access" icon={Database}         colorClass="" bgClass="bg-gradient-to-br from-indigo-400 to-indigo-600"  selected={selected} executionStatus={data?.executionStatus} />);
const SharePointSearchNode = memo(({ data, selected }: any) => <BaseNodeUI title="SharePoint Search" icon={FileSearch}       colorClass="" bgClass="bg-gradient-to-br from-indigo-400 to-indigo-600"  selected={selected} executionStatus={data?.executionStatus} isSource />);

// ── Full nodeTypes registry (mirrors FlowCanvas exactly) ─────────────────────
const VIEWER_NODE_TYPES = {
    remoteFile: RemoteFileNode,           REMOTE_FILE: RemoteFileNode,
    approval: ApprovalNode,               APPROVAL: ApprovalNode,
    fetchDocument: FetchDocumentNode,     FETCH_DOCUMENT: FetchDocumentNode,
    aiTransform: SmartTransformNode,      AI_TRANSFORM: SmartTransformNode,
    dataProfile: DataProfileNode,         DATA_PROFILE: DataProfileNode,
    source: SourceNode,                   SOURCE: SourceNode,
    ediSource: EdiSourceNode,             EDI_PARSE: EdiSourceNode,
    filter: FilterNode,                   FILTER: FilterNode,
    join: JoinNode,                       JOIN: JoinNode,
    aggregate: AggregateNode,             AGGREGATE: AggregateNode,
    formula: FormulaNode,                 FORMULA: FormulaNode,
    output: OutputNode,
    clean: CleanNode,                     CLEAN: CleanNode,
    smartImpute: SmartImputeNode,         SMART_IMPUTE: SmartImputeNode,
    column: ColumnNode,                   COLUMN: ColumnNode,
    pivot: PivotNode,                     PIVOT: PivotNode,
    signalStart: SignalStartNode,         SIGNAL_START: SignalStartNode,         SIGNALSTART: SignalStartNode,
    unpivot: UnpivotNode,                 UNPIVOT: UnpivotNode,
    autoType: AutoTypeNode,               AUTO_TYPE: AutoTypeNode,
    union: UnionNode,                     UNION: UnionNode,
    executeSql: ExecuteSqlNode,           EXECUTE_SQL: ExecuteSqlNode,
    sqlDml: SqlDmlNode,                   SQL_DML: SqlDmlNode,
    conditionalSplit: ConditionalSplitNode, CONDITIONAL_SPLIT: ConditionalSplitNode,
    split: ConditionalSplitNode,          SPLIT: ConditionalSplitNode,
    COND_SPLIT: ConditionalSplitNode,     cond_split: ConditionalSplitNode,
    destination: DestinationNode,         SINK: DestinationNode,
    salesforceDestination: SalesforceNode,
    trainModel: TrainModelNode,           ML_TRAIN: TrainModelNode,
    trainedModel: TrainedModelNode,       ML_TRAINED_MODEL: TrainedModelNode,
    scoreModel: ScoreModelNode,           ML_SCORE: ScoreModelNode,
    mlPredict: PredictModelNode,          predict: PredictModelNode,             ML_PREDICT: PredictModelNode,
    pareto: ParetoNode,                   PARETO: ParetoNode,
    window: WindowNode,                   WINDOW: WindowNode,
    evaluateModel: EvaluateModelNode,     ML_EVALUATE: EvaluateModelNode,
    sort: SortNode,                       SORT: SortNode,
    deduplicate: DeduplicateNode,         DEDUPLICATE: DeduplicateNode,
    aiInsight: AiInsightNode,             AI_INSIGHT: AiInsightNode,
    llmNode: LLMPromptNode,               LLM_NODE: LLMPromptNode,
    mdmIngest: MdmIngestNode,             MDM_INGEST: MdmIngestNode,
    annotation: AnnotationNode,           ANNOTATION: AnnotationNode,
    container: ContainerNode,             CONTAINER: ContainerNode,
    emailTrigger: EmailTriggerNode,       EMAIL_TRIGGER: EmailTriggerNode,       EMAILTRIGGER: EmailTriggerNode, fetchemail: EmailTriggerNode,
    eventListenerEmail: EmailListenerTriggerNode, EVENT_LISTENER_EMAIL: EmailListenerTriggerNode, EVENTLISTENEREMAIL: EmailListenerTriggerNode,
    emailAttributeFilter: EmailAttributeFilterNode, EMAIL_ATTRIBUTE_FILTER: EmailAttributeFilterNode,
    emailPatternFilter: EmailPatternFilterNode,     EMAIL_PATTERN_FILTER: EmailPatternFilterNode,
    attachmentFilter: AttachmentFilterNode,         ATTACHMENT_FILTER: AttachmentFilterNode,
    attachmentContentProcessor: AttachmentProcessorNode, ATTACHMENT_CONTENT_PROCESSOR: AttachmentProcessorNode,
    emailAction: EmailActionNode,         EMAIL_ACTION: EmailActionNode,         EMAILACTION: EmailActionNode,
    visual: VisualNode,                   VISUAL_PREVIEW: VisualNode,            VISUALPREVIEW: VisualNode,
    universalApi: UniversalApiNode,       UNIVERSAL_API: UniversalApiNode,       API_CALL: UniversalApiNode,    APICALL: UniversalApiNode,
    aiAgent: AIAgentNode,                 AI_AGENT: AIAgentNode,
    flowRunner: FlowRunnerNode,           FLOW_RUNNER: FlowRunnerNode,           FLOWRUNNER: FlowRunnerNode,
    forEach: ForEachNode,                 FOR_EACH: ForEachNode,                 FOREACH: ForEachNode,
    barrier: BarrierNode,                 BARRIER: BarrierNode,
    multiJoin: MultiJoinNode,             MULTI_JOIN: MultiJoinNode,             MULTIJOIN: MultiJoinNode,
    teams: TeamsNode,                     TEAMS_NOTIFY: TeamsNode,               TEAMS: TeamsNode,
    userAction: UserActionNode,           USER_ACTION: UserActionNode,           USERACTION: UserActionNode,
    webhook: WebhookNode,                 WEBHOOK_TRIGGER: WebhookNode,          WEBHOOK: WebhookNode,
    generateDocument: GenerateDocumentNode, GENERATE_DOCUMENT: GenerateDocumentNode, GENERATEDOCUMENT: GenerateDocumentNode,
    dataModel: DataModelNode,             DATA_MODEL: DataModelNode,             DATA_MODEL_OUTPUT: OutputNode,
    schedule: ScheduleNode,               SCHEDULE: ScheduleNode,
    fileListener: FileListenerNode,       FILE_LISTENER: FileListenerNode,
    documentParser: DocumentParserNode,   DOCUMENT_PARSER: DocumentParserNode,
    conditionalRouter: ConditionalRouterNode, CONDITIONAL_ROUTER: ConditionalRouterNode,
    dataMapper: DataMapperNode,           DATA_MAPPER: DataMapperNode,
    documentGenerator: DocumentGeneratorNode, DOCUMENT_GENERATOR: DocumentGeneratorNode,
    duplicateDetector: DuplicateDetectorNode, DUPLICATE_DETECTOR: DuplicateDetectorNode,
    pythonScript: PythonScriptNode,       PYTHON_SCRIPT: PythonScriptNode,
    loop: LoopNode,                       LOOP: LoopNode,
    contentMerger: ContentMergerNode,     CONTENT_MERGER: ContentMergerNode,
    aiSummarizer: AISummarizerNode,       AI_SUMMARIZER: AISummarizerNode,
    databaseLookup: DatabaseLookupNode,   DATABASE_LOOKUP: DatabaseLookupNode,
    templateRenderer: TemplateRendererNode, TEMPLATE_RENDERER: TemplateRendererNode,
    fileOutput: FileOutputNode,           FILE_OUTPUT: FileOutputNode,
    sharepointAccess: SharePointAccessNode, SHAREPOINT_ACCESS: SharePointAccessNode,
    sharepointSearch: SharePointSearchNode, SHAREPOINT_SEARCH: SharePointSearchNode,
    relaySource: RelaySourceNode,         RELAY_SOURCE: RelaySourceNode,         relay_source: RelaySourceNode,
    relaySink: RelaySinkNode,             RELAY_SINK: RelaySinkNode,             relay_sink: RelaySinkNode,
    date: DateNode,                       DATE: DateNode,
    geo: GeoNode,                         GEO: GeoNode,
    currency: CurrencyNode,               CURRENCY: CurrencyNode,
    // HubSpot CRM node
    hubspot: HubSpotNode,                 HUBSPOT: HubSpotNode,
    // File Source (tabular)
    fileSource: FileSourceNode,           FILE_SOURCE: FileSourceNode,           file_source: FileSourceNode,
    // File Object (binary — PDF, DOCX, images)
    fileObject: FileObjectNode,           FILE_OBJECT: FileObjectNode,
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtRows = (n: number | null | undefined) => n != null ? Number(n).toLocaleString() : null;
const fmtDuration = (ms: number | null | undefined) => {
    if (ms == null) return null;
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
};

// Map polling stepStatus.status → node executionStatus understood by BaseNodeUI
const mapStatus = (s: string): string => {
    const lo = (s || '').toLowerCase();
    if (['running', 'in_progress'].includes(lo)) return 'RUNNING';
    if (['completed', 'success', 'done', 'COMPLETED'].includes(lo)) return 'COMPLETED';
    if (['failed', 'error', 'FAILED'].includes(lo)) return 'FAILED';
    if (['waiting_approval', 'awaiting_approval', 'waiting'].includes(lo)) return 'WAITING_APPROVAL';
    if (['skipped'].includes(lo)) return 'SKIPPED';
    return '';
};

// ── Status legend chip ────────────────────────────────────────────────────────
const LegendChip = ({ color, label }: { color: string; label: string }) => (
    <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full ${color}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-current" />
        {label}
    </span>
);

// ── Status icon (top-right overlay on canvas header) ─────────────────────────
const StatusIcon = ({ status, size = 4 }: { status: string; size?: number }) => {
    const cls = `w-${size} h-${size}`;
    switch ((status || '').toLowerCase()) {
        case 'completed': case 'success': case 'done': return <CheckCircle2 className={`${cls} text-emerald-500`} />;
        case 'failed': case 'error':                   return <XCircle className={`${cls} text-red-500`} />;
        case 'running': case 'in_progress':            return <Loader2 className={`${cls} text-blue-500 animate-spin`} />;
        case 'waiting_approval': case 'waiting':       return <UserCheck className={`${cls} text-amber-500`} />;
        case 'skipped':                                return <SkipForward className={`${cls} text-gray-400`} />;
        case 'queued': case 'pending':                 return <Clock className={`${cls} text-slate-400`} />;
        default:                                       return <Play className={`${cls} text-gray-300`} />;
    }
};

// ── Props ─────────────────────────────────────────────────────────────────────
interface FlowRunViewerProps {
    /** Raw graph snapshot – { nodes: [...], edges: [...] } */
    flowSnapshot: { nodes: any[]; edges: any[] } | null;
    /** Current execution id (for display only) */
    executionId?: string | null;
    /** True while polling indicates the run hasn't finished */
    isExecuting: boolean;
    /** Live node statuses keyed by nodeId, provided by existing polling in TenantAppRunnerPage */
    stepStatus: Record<string, {
        status: string;
        rows_affected?: number | null;
        duration?: number | null;
        error_message?: string | null;
    }>;
    /** Called when user closes the canvas viewer */
    onClose: () => void;
    /** Current run record for the header badge */
    currentRun?: { status?: string; id?: string } | null;
}

// ── Inner canvas (wrapped by ReactFlowProvider below) ────────────────────────
function FlowRunViewerInner({
    flowSnapshot,
    executionId,
    isExecuting,
    stepStatus,
    onClose,
    currentRun,
}: FlowRunViewerProps) {
    const memoNodeTypes = useMemo(() => VIEWER_NODE_TYPES, []);
    const [isFullScreen, setIsFullScreen] = useState(false);

    // ── Build initial nodes / edges from snapshot ─────────────────────────────
    const initialNodes: Node[] = useMemo(() => {
        if (!flowSnapshot?.nodes) return [];
        return flowSnapshot.nodes.map((n: any) => ({
            id: n.id,
            type: n.type,
            position: n.position || { x: 0, y: 0 },
            data: {
                ...(n.data || n.config || {}),
                label: n.data?.label || n.name || n.id,
                // Inject initial executionStatus from stepStatus (handles replay of a finished run)
                executionStatus: mapStatus((stepStatus[n.id]?.status) || ''),
            },
            parentNode: n.parentNode || n.parentId || undefined,
            extent: n.extent,
            style: n.style,
            width: n.width,
            height: n.height,
        }));
    }, [flowSnapshot]); // intentionally not re-derived from stepStatus after init

    const initialEdges: Edge[] = useMemo(() => {
        if (!flowSnapshot?.edges) return [];
        return flowSnapshot.edges.map((e: any) => ({
            ...e,
            style: { stroke: '#94a3b8', strokeWidth: 1.5, ...e.style },
            animated: isExecuting,
        }));
    }, [flowSnapshot, isExecuting]);

    const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

    // ── Sync snapshot → nodes when flowSnapshot changes (new content selected) ─
    useEffect(() => {
        if (!flowSnapshot?.nodes) return;
        setNodes(flowSnapshot.nodes.map((n: any) => ({
            id: n.id,
            type: n.type,
            position: n.position || { x: 0, y: 0 },
            data: {
                ...(n.data || n.config || {}),
                label: n.data?.label || n.name || n.id,
                executionStatus: mapStatus((stepStatus[n.id]?.status) || ''),
            },
            parentNode: n.parentNode || n.parentId || undefined,
            extent: n.extent,
            style: n.style,
        })));
        setEdges(flowSnapshot.edges?.map((e: any) => ({
            ...e,
            style: { stroke: '#94a3b8', strokeWidth: 1.5, ...e.style },
            animated: isExecuting,
        })) ?? []);
    }, [flowSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Apply real-time status overlays when stepStatus changes ──────────────
    useEffect(() => {
        if (!Object.keys(stepStatus).length) return;
        setNodes(prev => prev.map(n => {
            const s = stepStatus[n.id];
            if (!s) return n;
            const execStatus = mapStatus(s.status);
            if (n.data?.executionStatus === execStatus) return n; // no change
            return {
                ...n,
                data: {
                    ...n.data,
                    executionStatus: execStatus,
                    rowsAffected: s.rows_affected ?? n.data?.rowsAffected,
                    error: s.error_message ?? n.data?.error,
                },
            };
        }));

        // Colour edges based on source node status
        setEdges(prev => prev.map(e => {
            const src = stepStatus[e.source];
            if (!src) return { ...e, animated: isExecuting };
            const lo = (src.status || '').toLowerCase();
            const isRunning = ['running', 'in_progress'].includes(lo);
            const isDone    = ['completed', 'success', 'done'].includes(lo);
            const isFailed  = ['failed', 'error'].includes(lo);
            return {
                ...e,
                animated: isRunning,
                style: {
                    ...e.style,
                    stroke: isDone ? '#10b981' : isFailed ? '#ef4444' : isRunning ? '#3b82f6' : '#94a3b8',
                    strokeWidth: (isDone || isRunning) ? 2 : 1.5,
                },
                label: isDone && src.rows_affected != null ? `${fmtRows(src.rows_affected)} rows` : undefined,
                labelStyle: { fill: '#10b981', fontWeight: 600, fontSize: 10 },
            };
        }));
    }, [stepStatus, isExecuting]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Stats summary from stepStatus ─────────────────────────────────────────
    const stats = useMemo(() => {
        const vals = Object.values(stepStatus);
        const total     = vals.length;
        const completed = vals.filter(s => ['completed', 'success', 'done'].includes((s.status || '').toLowerCase())).length;
        const running   = vals.filter(s => ['running', 'in_progress'].includes((s.status || '').toLowerCase())).length;
        const failed    = vals.filter(s => ['failed', 'error'].includes((s.status || '').toLowerCase())).length;
        const totalRows = vals.reduce((acc, s) => acc + (s.rows_affected ?? 0), 0);
        return { total, completed, running, failed, totalRows };
    }, [stepStatus]);

    const overallStatus = currentRun?.status || (isExecuting ? 'running' : 'idle');

    return (
        <div className={`flex flex-col bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ${isFullScreen ? 'fixed inset-2 z-50' : 'h-full w-full'}`}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <div className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-800/60">
                <div className="flex items-center gap-2.5">
                    <Eye className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-bold text-gray-800 dark:text-slate-100">Live Canvas</span>
                    {/* Run ID badge */}
                    {executionId && (
                        <span className="text-[10px] font-mono text-gray-400 dark:text-slate-500 bg-gray-100 dark:bg-slate-700 px-2 py-0.5 rounded">
                            #{(executionId).slice(-8).toUpperCase()}
                        </span>
                    )}
                    {/* Overall status */}
                    <span className="flex items-center gap-1">
                        <StatusIcon status={overallStatus} size={3} />
                        <span className={`text-[10px] font-semibold uppercase tracking-wide ${
                            overallStatus.toLowerCase().includes('complet') ? 'text-emerald-600' :
                            overallStatus.toLowerCase().includes('fail')    ? 'text-red-500' :
                            overallStatus.toLowerCase().includes('run')     ? 'text-blue-500' :
                            'text-gray-400'
                        }`}>{overallStatus}</span>
                    </span>
                </div>

                <div className="flex items-center gap-2">
                    {/* Progress stats */}
                    {stats.total > 0 && (
                        <div className="hidden sm:flex items-center gap-1.5">
                            <LegendChip color="text-emerald-700 bg-emerald-50 dark:bg-emerald-900/20" label={`${stats.completed} done`} />
                            {stats.running > 0  && <LegendChip color="text-blue-700 bg-blue-50 dark:bg-blue-900/20"       label={`${stats.running} running`} />}
                            {stats.failed  > 0  && <LegendChip color="text-red-700 bg-red-50 dark:bg-red-900/20"           label={`${stats.failed} failed`} />}
                            {stats.totalRows > 0 && <LegendChip color="text-gray-600 bg-gray-100 dark:bg-slate-700"       label={`${fmtRows(stats.totalRows)} rows`} />}
                        </div>
                    )}
                    <button
                        onClick={() => setIsFullScreen(f => !f)}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        title={isFullScreen ? 'Exit full screen' : 'Full screen'}
                    >
                        {isFullScreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                        title="Close canvas view"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            </div>

            {/* ── React Flow Canvas ────────────────────────────────────────── */}
            <div className="flex-1 min-h-0 relative">
                {!flowSnapshot ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <p className="text-sm text-gray-400 dark:text-slate-500">No flow structure available.</p>
                    </div>
                ) : (
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        nodeTypes={memoNodeTypes}
                        // ── Read-only settings ──────────────────────────────
                        nodesDraggable={false}
                        nodesConnectable={false}
                        elementsSelectable={false}
                        panOnDrag={true}
                        zoomOnScroll={true}
                        zoomOnPinch={true}
                        preventScrolling={false}
                        fitView
                        fitViewOptions={{ padding: 0.15 }}
                        minZoom={0.1}
                        maxZoom={2}
                        proOptions={{ hideAttribution: true }}
                        className="bg-slate-50 dark:bg-slate-900"
                    >
                        <Background color="#e2e8f0" gap={20} size={1} className="dark:!bg-slate-900" />
                        <Controls showInteractive={false} className="!bottom-4 !left-4" />
                        <MiniMap
                            nodeStrokeWidth={3}
                            className="!bottom-4 !right-4 !rounded-lg !border !border-gray-200 dark:!border-slate-700"
                            nodeColor={(n: Node) => {
                                const st = (n.data?.executionStatus || '').toLowerCase();
                                if (st === 'running')   return '#3b82f6';
                                if (st === 'completed') return '#10b981';
                                if (st === 'failed')    return '#ef4444';
                                return '#94a3b8';
                            }}
                            maskColor="rgba(241,245,249,0.6)"
                        />

                        {/* ── Animated progress bar while running ─────────── */}
                        {isExecuting && (
                            <div className="absolute top-0 left-0 right-0 h-0.5 z-10 overflow-hidden bg-blue-100">
                                <div
                                    className="h-full bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400"
                                    style={{
                                        width: '40%',
                                        animation: 'slideProgress 1.6s ease-in-out infinite',
                                    }}
                                />
                            </div>
                        )}
                    </ReactFlow>
                )}
            </div>

            {/* ── Inline keyframes for the progress bar ────────────────────── */}
            <style>{`
                @keyframes slideProgress {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(350%); }
                }
            `}</style>
        </div>
    );
}

// ── Public export — wraps the inner component with ReactFlowProvider ──────────
export default function FlowRunViewer(props: FlowRunViewerProps) {
    return (
        <ReactFlowProvider>
            <FlowRunViewerInner {...props} />
        </ReactFlowProvider>
    );
}
